import { Buffer } from 'buffer'
import {
	address as bitcoinAddress,
	crypto as bitcoinCrypto,
	script as bitcoinScript,
	Transaction,
	type Psbt
} from 'bitcoinjs-lib'
import { parsePsbt } from './market/secure-psbt'
import { hasOfferDieselMint, hasOfferFundingMint } from './offer-diesel'
import { normalizeOutpoint } from './item-listing'
import { OfferError } from './offer-errors'

/**
 * Buyer-side checks on the transactions the offers service asks the wallet
 * to sign. The wallet signs whatever it is handed, so every template is
 * pinned to the offer the buyer reviewed before a signature is requested,
 * and every signature the wallet hands back is checked before it leaves the
 * page:
 *  - the funding spends only the buyer's payment coins, with SIGHASH_ALL or
 *    DEFAULT, into exactly the escrow, the vault mint and change, at a
 *    bounded miner fee;
 *  - the escrow input of every pre-signed template carries the 2-of-2 sale
 *    leaf and nothing else (never the CSV recovery leaf, which a
 *    NONE|ANYONECANPAY signature would turn into a sweep), with the version,
 *    locktime and sequence the API's templates use;
 *  - the item sale delivers the item to the buyer, the price to the seller
 *    and the quoted fee to the market, and nothing else.
 * These mirror validateOfferSettlement and validateOfferRefund on the seller
 * side.
 */

export const SIGHASH_DEFAULT = 0x00
export const SIGHASH_ALL = 0x01
/** NONE|ANYONECANPAY: how a collection or trait offer authorizes its escrow. */
export const SIGHASH_NONE_ANYONECANPAY = 0x82
/** What the API's accept, scope and fill templates set (offers.rs TX_VERSION, ZERO, ENABLE_RBF_NO_LOCKTIME). */
export const OFFER_TX_VERSION = 2
export const OFFER_TX_LOCKTIME = 0
export const OFFER_TX_SEQUENCE = 0xfffffffd
export const TAPSCRIPT_LEAF_VERSION = 0xc0
/** BIP-341 provably unspendable internal key every offer escrow uses. */
export const NUMS_INTERNAL_KEY = Buffer.from(
	'50929b74c1a04954b78b4b6035e97a5e078a5a0f28ec96d547bfee9ace803ac0',
	'hex'
)

/** Shown once a funding is signed: it exists, but it never left the page. */
export const NOT_PLACED = 'Your offer was not placed. The signed funding was not broadcast.'

const FUNDING_SIGHASHES = new Set([SIGHASH_DEFAULT, SIGHASH_ALL])

const toScript = (address: string): Buffer => bitcoinAddress.toOutputScript(address)
const same = (a: Uint8Array | undefined | null, b: Uint8Array): boolean =>
	!!a && Buffer.from(a).equals(Buffer.from(b))
const outpointOf = (psbt: Psbt, index: number): string | null => {
	const input = psbt.txInputs[index]
	return input ? `${Buffer.from(input.hash).reverse().toString('hex')}:${input.index}` : null
}
const unsignedTx = (psbt: Psbt): Buffer => psbt.data.globalMap.unsignedTx.toBuffer()

/** The prevout of one input, from its witness or non-witness UTXO. */
export function psbtInputPrevout(psbt: Psbt, index: number): { script: Buffer; value: number } | null {
	const data = psbt.data.inputs[index]
	if (!data) return null
	if (data.witnessUtxo?.script)
		return { script: Buffer.from(data.witnessUtxo.script), value: data.witnessUtxo.value }
	if (data.nonWitnessUtxo) {
		try {
			const tx = Transaction.fromBuffer(Buffer.from(data.nonWitnessUtxo))
			const prev = psbt.txInputs[index]
			// A non-witness UTXO that is not the spent transaction proves nothing.
			if (!Buffer.from(prev.hash).equals(tx.getHash())) return null
			const out = tx.outs[prev.index]
			return out ? { script: Buffer.from(out.script), value: out.value } : null
		} catch {
			return null
		}
	}
	return null
}

/** The prevout script of one input, from its witness or non-witness UTXO. */
export function psbtInputScript(psbt: Psbt, index: number): Buffer | null {
	return psbtInputPrevout(psbt, index)?.script ?? null
}

/** Rough virtual size of one input spending `script`, for the fee ceiling. */
function inputVbytes(script: Buffer): number {
	if (script.length === 34 && script[0] === 0x51) return 58 // P2TR key path
	if (script.length === 22 && script[0] === 0x00) return 68 // P2WPKH
	if (script.length === 23 && script[0] === 0xa9) return 91 // P2SH-P2WPKH
	return 148
}

export interface FundingTemplateExpectation {
	/** The escrow scriptPubKey reconstructed from the verified intent. */
	escrowScript: Buffer
	escrowValue: number
	/** The buyer's payment address: the only coins a funding may spend, and its change. */
	paymentAddress: string
	/** The fee rate the buyer chose; bounds what the funding may pay miners. */
	feeRate: number
}

export interface FundingTemplateSummary {
	inputCount: number
	escrowVout: number
	changeVout: number | null
	/** Whether the funding carries the 0-value DIESEL vault mint. */
	mint: boolean
	minerFeeSats: number
}

/**
 * Every input must be one of the buyer's payment coins, asking for
 * SIGHASH_ALL or DEFAULT, and the outputs must be exactly: the one escrow
 * output, at most one 0-value OP_RETURN carrying the offer's DIESEL mint (in
 * any position, as the API's coin selector places change after it), and at
 * most one change output back to the payment address. The miner fee is
 * bounded by twice the chosen rate over a generous size estimate. Anything
 * else is a funding transaction the buyer did not ask for.
 */
export function validateOfferFundingTemplate(
	psbtHex: string,
	expected: FundingTemplateExpectation
): FundingTemplateSummary {
	const fail = (message: string) => new OfferError('funding_template_invalid', message)
	let psbt: Psbt
	try {
		psbt = parsePsbt(psbtHex)
	} catch {
		throw fail('The funding transaction could not be read. Nothing was signed.')
	}
	if (!Number.isSafeInteger(expected.escrowValue) || expected.escrowValue <= 0)
		throw fail('The offer has no escrow amount. Nothing was signed.')
	if (!Number.isFinite(expected.feeRate) || expected.feeRate < 1 || expected.feeRate > 500)
		throw fail('The offer has no valid fee rate. Nothing was signed.')
	const paymentScript = toScript(expected.paymentAddress)
	if (psbt.inputCount < 1) throw fail('The funding transaction has no inputs. Nothing was signed.')
	let inputSats = 0
	let vbytes = 10.5
	for (let index = 0; index < psbt.inputCount; index++) {
		const prevout = psbtInputPrevout(psbt, index)
		// A wallet with separate payment and ordinals addresses funds from the
		// payment address only; the ordinals address holds inscriptions.
		if (!prevout || !prevout.script.equals(paymentScript))
			throw fail(
				`The funding transaction spends a coin (input ${index}) that is not in your payment wallet. Nothing was signed.`
			)
		const data = psbt.data.inputs[index]
		if (data.sighashType !== undefined && !FUNDING_SIGHASHES.has(data.sighashType))
			throw fail(
				`The funding transaction asks for a reusable signature on input ${index}. Nothing was signed.`
			)
		if (data.tapLeafScript?.length)
			throw fail(`The funding transaction attaches a script to input ${index}. Nothing was signed.`)
		inputSats += prevout.value
		vbytes += inputVbytes(prevout.script)
	}
	let escrowVout = -1
	let changeVout = -1
	let opReturns = 0
	let outputSats = 0
	psbt.txOutputs.forEach((output, vout) => {
		const script = Buffer.from(output.script)
		outputSats += output.value
		vbytes += 9 + script.length
		if (script.equals(expected.escrowScript) && output.value === expected.escrowValue) {
			if (escrowVout >= 0)
				throw fail('The funding transaction funds the escrow twice. Nothing was signed.')
			escrowVout = vout
			return
		}
		if (script[0] === 0x6a) {
			opReturns++
			if (output.value !== 0 || opReturns > 1)
				throw fail('The funding transaction carries an unexpected data output. Nothing was signed.')
			return
		}
		if (script.equals(paymentScript) && output.value > 0) {
			if (changeVout >= 0)
				throw fail('The funding transaction returns change twice. Nothing was signed.')
			changeVout = vout
			return
		}
		throw fail(
			`The funding transaction pays an output (${vout}) that is not part of this offer. Nothing was signed.`
		)
	})
	if (escrowVout < 0)
		throw fail('The funding transaction does not fund this offer’s escrow. Nothing was signed.')
	if (opReturns === 1 && !hasOfferFundingMint(psbt.txOutputs))
		throw fail('The funding transaction carries an unexpected data output. Nothing was signed.')
	const minerFeeSats = inputSats - outputSats
	const ceiling = Math.ceil(2 * expected.feeRate * vbytes) + 2_000
	if (minerFeeSats < 0 || minerFeeSats > ceiling)
		throw fail(
			`The funding transaction pays ${minerFeeSats.toLocaleString('en-US')} sats in network fees, far more than the fee you chose. Nothing was signed.`
		)
	return {
		inputCount: psbt.inputCount,
		escrowVout,
		changeVout: changeVout >= 0 ? changeVout : null,
		mint: opReturns === 1,
		minerFeeSats
	}
}

/** DER signatures carry their sighash as the last byte. */
const isDer = (chunk: unknown): chunk is Buffer =>
	Buffer.isBuffer(chunk) && chunk.length >= 9 && chunk.length <= 73 && chunk[0] === 0x30

function witnessItems(witness: Uint8Array): Buffer[] {
	const buffer = Buffer.from(witness)
	const items: Buffer[] = []
	let offset = 0
	const readVarInt = () => {
		const first = buffer[offset]
		if (first < 0xfd) {
			offset += 1
			return first
		}
		if (first === 0xfd) {
			offset += 3
			return buffer.readUInt16LE(offset - 2)
		}
		if (first === 0xfe) {
			offset += 5
			return buffer.readUInt32LE(offset - 4)
		}
		offset += 9
		return Number(buffer.readBigUInt64LE(offset - 8))
	}
	const count = readVarInt()
	for (let i = 0; i < count; i++) {
		const length = readVarInt()
		items.push(buffer.subarray(offset, offset + length))
		offset += length
	}
	return items
}

/**
 * Every sighash byte the wallet committed to on one input, however it
 * returned the signature (PSBT fields or a finalized witness/scriptSig).
 * Returns null when the input carries a script-path signature, which a
 * funding input never should.
 */
function fundingInputSighashes(input: Psbt['data']['inputs'][number]): number[] | null {
	if (input.tapScriptSig?.length) return null
	const found: number[] = []
	if (input.tapKeySig?.length) found.push(input.tapKeySig.length === 65 ? input.tapKeySig[64] : SIGHASH_DEFAULT)
	for (const partial of input.partialSig ?? []) found.push(partial.signature[partial.signature.length - 1])
	if (input.finalScriptWitness?.length) {
		const items = witnessItems(input.finalScriptWitness)
		if (items.length === 1)
			found.push(items[0].length === 65 ? items[0][64] : SIGHASH_DEFAULT) // Taproot key path
		else if (items.length === 2 && isDer(items[0])) found.push(items[0][items[0].length - 1]) // P2WPKH
		else return null
	}
	if (input.finalScriptSig?.length) {
		for (const chunk of bitcoinScript.decompile(Buffer.from(input.finalScriptSig)) ?? [])
			if (isDer(chunk)) found.push(chunk[chunk.length - 1])
	}
	return found
}

/**
 * After the wallet signs the funding: the transaction is the one reviewed,
 * and every signature commits to the whole transaction (ALL or DEFAULT), so
 * none can be lifted into another transaction that redirects the change.
 */
export function assertSignedFunding(templateHex: string, signedHex: string): void {
	const fail = (message: string) => new OfferError('funding_signature_invalid', message)
	let template: Psbt
	let signed: Psbt
	try {
		template = parsePsbt(templateHex)
		signed = parsePsbt(signedHex)
	} catch {
		throw fail('The wallet returned a funding transaction that could not be read. Nothing was broadcast.')
	}
	if (!unsignedTx(template).equals(unsignedTx(signed)))
		throw fail('The wallet changed the funding transaction. Nothing was broadcast.')
	signed.data.inputs.forEach((input, index) => {
		const sighashes = fundingInputSighashes(input)
		if (!sighashes || sighashes.some((sighash) => !FUNDING_SIGHASHES.has(sighash)))
			throw fail(
				`Your wallet signed funding input ${index} in a way that could be reused elsewhere. Nothing was broadcast.`
			)
	})
}

export interface EscrowOutpoint {
	txid: string
	vout: number
	value: number
	script: Buffer
	/** The 2-of-2 sale leaf and its control block: the only leaf a template may carry. */
	saleLeaf: Buffer
	saleControl: Buffer
}

export const tapLeafHash = (leaf: Buffer): Buffer =>
	bitcoinCrypto.taggedHash(
		'TapLeaf',
		Buffer.concat([Buffer.from([TAPSCRIPT_LEAF_VERSION]), varSlice(leaf)])
	)
const varSlice = (data: Buffer): Buffer => {
	const n = data.length
	const prefix =
		n < 0xfd
			? Buffer.from([n])
			: n <= 0xffff
			? Buffer.from([0xfd, n & 0xff, n >> 8])
			: Buffer.from([0xfe, n & 0xff, (n >> 8) & 0xff, (n >> 16) & 0xff, (n >> 24) & 0xff])
	return Buffer.concat([prefix, data])
}

/**
 * The escrow input of a template the buyer pre-signs. Only the sale leaf
 * may be attached: wallets (the native signer and the extensions alike) sign
 * every leaf that names their key, and a recovery-leaf signature under
 * NONE|ANYONECANPAY would let anyone sweep the escrow once the CSV delay
 * passes. No key path either: the internal key is the NUMS point.
 */
function assertEscrowInput(
	psbt: Psbt,
	index: number,
	escrow: EscrowOutpoint,
	fail: (message: string) => OfferError
) {
	const input = psbt.data.inputs[index]
	const leaves = input?.tapLeafScript ?? []
	if (
		outpointOf(psbt, index) !== `${escrow.txid.toLowerCase()}:${escrow.vout}` ||
		input?.witnessUtxo?.value !== escrow.value ||
		!same(input?.witnessUtxo?.script, escrow.script)
	)
		throw fail(`The transaction does not spend this offer’s escrow. ${NOT_PLACED}`)
	if (
		leaves.length !== 1 ||
		leaves[0].leafVersion !== TAPSCRIPT_LEAF_VERSION ||
		!same(leaves[0].script, escrow.saleLeaf) ||
		!same(leaves[0].controlBlock, escrow.saleControl)
	)
		throw fail(`The transaction attaches an escrow script this offer did not agree to. ${NOT_PLACED}`)
	if (input.tapInternalKey && !same(input.tapInternalKey, NUMS_INTERNAL_KEY))
		throw fail(`The transaction names an escrow key this offer did not agree to. ${NOT_PLACED}`)
	if (psbt.txInputs[index].sequence !== OFFER_TX_SEQUENCE)
		throw fail(`The transaction sets an unexpected input sequence. ${NOT_PLACED}`)
}

function assertTemplateFraming(psbt: Psbt, fail: (message: string) => OfferError) {
	if (psbt.version !== OFFER_TX_VERSION || psbt.locktime !== OFFER_TX_LOCKTIME)
		throw fail(`The transaction sets an unexpected version or lock time. ${NOT_PLACED}`)
}

export interface AcceptTemplateExpectation {
	/** The input the API asks the buyer to sign; always the escrow at index 1. */
	signInputIndex: number
	/** `txid:vout`, or the 72-hex serialized form the intent carries. */
	itemOutpoint: string
	itemValue: number
	sellerAddress: string
	buyerAddress: string
	escrow: EscrowOutpoint
	priceSats: number
	/** The fee the build quoted, already net of the bid rebate. */
	marketFeeSats: number
	feeAddress: string
}

/**
 * The item settlement the buyer pre-signs with SIGHASH_ALL: input 0 is the
 * item, input 1 the escrow; output 0 returns the item's whole output value
 * to the buyer, output 1 pays the seller, output 2 pays the market fee, and
 * an optional 0-value DIESEL mint closes the list.
 */
export function validateOfferAcceptTemplate(
	psbtHex: string,
	expected: AcceptTemplateExpectation
): Psbt {
	const fail = (message: string) => new OfferError('accept_template_invalid', message)
	let psbt: Psbt
	try {
		psbt = parsePsbt(psbtHex)
	} catch {
		throw fail(`The sale transaction could not be read. ${NOT_PLACED}`)
	}
	const itemOutpoint = normalizeOutpoint(expected.itemOutpoint)
	if (!itemOutpoint) throw fail(`The offer names no item output. ${NOT_PLACED}`)
	if (
		!Number.isSafeInteger(expected.itemValue) ||
		expected.itemValue <= 0 ||
		!Number.isSafeInteger(expected.priceSats) ||
		expected.priceSats <= 0 ||
		!Number.isSafeInteger(expected.marketFeeSats) ||
		expected.marketFeeSats < 0
	)
		throw fail(`The offer amounts could not be read. ${NOT_PLACED}`)
	if (psbt.inputCount !== 2 || expected.signInputIndex !== 1)
		throw fail(`The sale transaction does not have the item and the escrow as its inputs. ${NOT_PLACED}`)
	assertTemplateFraming(psbt, fail)
	const item = psbt.data.inputs[0]
	if (
		outpointOf(psbt, 0) !== itemOutpoint ||
		item.witnessUtxo?.value !== expected.itemValue ||
		(item.sighashType !== undefined && item.sighashType !== SIGHASH_ALL) ||
		psbt.txInputs[0].sequence !== OFFER_TX_SEQUENCE
	)
		throw fail(`The sale transaction does not spend the item this offer is for. ${NOT_PLACED}`)
	assertEscrowInput(psbt, 1, expected.escrow, fail)
	if (psbt.data.inputs[1].sighashType !== SIGHASH_ALL)
		throw fail(`The sale transaction does not pin every output with your signature. ${NOT_PLACED}`)
	const outputs = psbt.txOutputs
	if (outputs.length !== 3 && outputs.length !== 4)
		throw fail(`The sale transaction has unexpected outputs. ${NOT_PLACED}`)
	if (
		!same(outputs[0].script, toScript(expected.buyerAddress)) ||
		outputs[0].value !== expected.itemValue
	)
		throw fail(`The sale transaction does not deliver the item to your wallet. ${NOT_PLACED}`)
	if (
		!same(outputs[1].script, toScript(expected.sellerAddress)) ||
		outputs[1].value !== expected.priceSats
	)
		throw fail(`The sale transaction does not pay the seller the offer price. ${NOT_PLACED}`)
	if (
		!same(outputs[2].script, toScript(expected.feeAddress)) ||
		outputs[2].value !== expected.marketFeeSats
	)
		throw fail(`The sale transaction does not pay the quoted market fee. ${NOT_PLACED}`)
	if (outputs.length === 4 && !hasOfferDieselMint(outputs))
		throw fail(`The sale transaction carries an unexpected fourth output. ${NOT_PLACED}`)
	if (outputs.reduce((sum, output) => sum + output.value, 0) > expected.itemValue + expected.escrow.value)
		throw fail(`The sale transaction spends more than it funds. ${NOT_PLACED}`)
	return psbt
}

export interface ScopeTemplateExpectation {
	signInputIndex: number
	escrow: EscrowOutpoint
}

/**
 * The collection or trait authorization: the escrow alone, signed with
 * NONE|ANYONECANPAY, so the outputs are placeholders the signature never
 * commits to. The input, its value, its only leaf, its sequence and the
 * transaction's version and lock time (which the signature does commit to)
 * are what matter.
 */
export function validateOfferScopeTemplate(
	psbtHex: string,
	expected: ScopeTemplateExpectation
): Psbt {
	const fail = (message: string) => new OfferError('accept_template_invalid', message)
	let psbt: Psbt
	try {
		psbt = parsePsbt(psbtHex)
	} catch {
		throw fail(`The offer authorization could not be read. ${NOT_PLACED}`)
	}
	if (psbt.inputCount !== 1 || expected.signInputIndex !== 0)
		throw fail(`The offer authorization must spend only this offer’s escrow. ${NOT_PLACED}`)
	assertTemplateFraming(psbt, fail)
	assertEscrowInput(psbt, 0, expected.escrow, fail)
	if (psbt.data.inputs[0].sighashType !== SIGHASH_NONE_ANYONECANPAY)
		throw fail(`The offer authorization asks for an unexpected signature type. ${NOT_PLACED}`)
	return psbt
}

const hasSignature = (input: Psbt['data']['inputs'][number] | undefined) =>
	!!input &&
	(!!input.tapKeySig?.length ||
		!!input.tapScriptSig?.length ||
		!!input.partialSig?.length ||
		!!input.finalScriptWitness?.length ||
		!!input.finalScriptSig?.length)

/**
 * After the wallet pre-signs the escrow, for every wallet type: the
 * transaction is the template, the escrow input carries only sale-leaf
 * signatures by the buyer's key with the expected sighash (no key-path
 * signature, no finalized witness, no signature on any other leaf), and no
 * other input was signed.
 */
export function assertSignedEscrow(
	templateHex: string,
	signedHex: string,
	expected: { index: number; escrow: EscrowOutpoint; buyerXOnly: string; sighash: number }
): void {
	const fail = (message: string) => new OfferError('escrow_signature_invalid', message)
	let template: Psbt
	let signed: Psbt
	try {
		template = parsePsbt(templateHex)
		signed = parsePsbt(signedHex)
	} catch {
		throw fail(`The wallet returned an offer signature that could not be read. ${NOT_PLACED}`)
	}
	if (!unsignedTx(template).equals(unsignedTx(signed)))
		throw fail(`The wallet changed the offer transaction. ${NOT_PLACED}`)
	const saleHash = tapLeafHash(expected.escrow.saleLeaf)
	const buyer = Buffer.from(expected.buyerXOnly, 'hex')
	signed.data.inputs.forEach((input, index) => {
		if (index !== expected.index) {
			if (hasSignature(input))
				throw fail(`The wallet signed an input that is not part of this offer. ${NOT_PLACED}`)
			return
		}
		const sigs = input.tapScriptSig ?? []
		if (
			input.tapKeySig?.length ||
			input.finalScriptWitness?.length ||
			input.finalScriptSig?.length ||
			input.partialSig?.length ||
			sigs.length === 0 ||
			!sigs.every(
				(sig) =>
					same(sig.leafHash, saleHash) &&
					same(sig.pubkey, buyer) &&
					sig.signature.length === 65 &&
					sig.signature[64] === expected.sighash
			)
		)
			throw fail(`The wallet signed the escrow outside this offer’s terms. ${NOT_PLACED}`)
	})
}
